// Preferencia "Mostrar comidas" en el calendario `/entrenos` (M2.34.C).
//
// Persistida en localStorage bajo `tm:calendar:show-meals` (valores 'true' /
// 'false'). Default ON.
//
// Implementada como `useSyncExternalStore` para que múltiples componentes
// (`CalendarToolbar` + `DayView`) se mantengan en sync sin pasar prop drilling
// ni context provider — el calendar es un único árbol, y la pref vive en el
// browser, no en el server.

import { useSyncExternalStore } from 'react';

export const SHOW_MEALS_STORAGE_KEY = 'tm:calendar:show-meals';

function read(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(SHOW_MEALS_STORAGE_KEY);
    if (raw === null) return true; // default ON
    return raw === 'true';
  } catch {
    return true;
  }
}

const listeners = new Set<() => void>();

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  // También escuchamos el evento nativo `storage` para mantenernos en sync
  // si otra pestaña del navegador cambia el valor.
  const storageHandler = (e: StorageEvent): void => {
    if (e.key === SHOW_MEALS_STORAGE_KEY) cb();
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', storageHandler);
  }
  return () => {
    listeners.delete(cb);
    if (typeof window !== 'undefined') {
      window.removeEventListener('storage', storageHandler);
    }
  };
}

function notify(): void {
  for (const l of listeners) l();
}

export function setShowMealsPref(value: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SHOW_MEALS_STORAGE_KEY, String(value));
    notify();
  } catch {
    // Ignorar: localStorage puede estar bloqueado (modo privado).
  }
}

export function useShowMealsPref(): boolean {
  return useSyncExternalStore(
    subscribe,
    read,
    () => true, // SSR fallback (no aplica aquí pero requerido por el API)
  );
}
